import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import PrivateRoute from 'js/components/route_utils/PrivateRoute';
import { ROUTE_SETTINGS, ROUTE_SETTINGS_USER } from 'new/constants/pathnames';
import Drawer from 'new/components/drawer/Drawer';
import { router } from 'new/routes/router';
import { fetchAllOrgs, loadTeams, setContextualSupport, unsetContextualSupport } from 'js/actions';
import { useDispatch } from 'react-redux';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useImpersonatedUser } from 'new/hooks/adapters/useImpersonatedUser';
import { SUPPORT_KEY_ACCOUNT } from 'js/constants/supportPageKeys';
import structure from 'new/routes/structure';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useSectionNavigation, useLastVisitedPath } from 'new/hooks/useSectionNavigation';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { filterNavigationContentByFeatureFlags } from 'new/routes/routes.util';

type CurrentUser = ReturnType<typeof useCurrentUser>;

const SIDEBAR_GROUPS = (user: CurrentUser, impersonated: boolean) =>
  structure.find((s) => s.name === 'Settings').groups?.(user, impersonated);

export default function SettingsContainer() {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const user = useCurrentUser();
  const impersonated = useImpersonatedUser();
  const capsulesSettingsEnabled = useFeatureFlag('UI_ENABLE_CAPSULES_SETTINGS');

  useSectionNavigation('settings');

  useEffect(() => {
    dispatch(fetchAllOrgs());
    dispatch(loadTeams());
  }, []);

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_ACCOUNT));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_ACCOUNT));
    };
  }, []);

  const defaultPath = `${ROUTE_REDESIGN}${ROUTE_SETTINGS_USER}`;
  const lastVisitedPath = useLastVisitedPath('settings', ROUTE_SETTINGS, defaultPath);

  const handledSidebarGroups = useMemo(() => {
    const groups = SIDEBAR_GROUPS(user, Boolean(impersonated)) ?? [];
    return filterNavigationContentByFeatureFlags(groups);
  }, [user, impersonated, capsulesSettingsEnabled]);

  return (
    <Drawer content={handledSidebarGroups as any}>
      <Switch>
        {Object.keys(router.routesNames.settings).map((routeName) => (
          <PrivateRoute key={routeName} {...router.manager.getRoute(routeName, 'settings')} />
        ))}
        <Route path="*">
          {lastVisitedPath ? <Redirect to={lastVisitedPath} /> : <Redirect to={defaultPath} />}
        </Route>
      </Switch>
    </Drawer>
  );
}
